import { useEffect, useState } from "react";
import { FaMapMarkerAlt, FaWarehouse, FaBox } from "react-icons/fa";
import { Snowflake, Bell } from "lucide-react";
import { getAllLocalisations } from "../../services/localisations";
import { getAllEntrepots } from "../../services/entrepots";
import { getAllChambresFroides } from "../../services/chambresfroides";
import { getConteneurs } from "../../services/conteneurs";
import { getAlertes } from "../../services/alertes";
import { getReleves } from "../../services/releves";
import { getSeuils } from "../../services/seuils";

import type { Localisation } from "../../types/localisation";
import type { Entrepot } from "../../types/entrepot";
import type { ChambreFroide } from "../../types/chambrefroide";
import type { Conteneur } from "../../types/conteneur";
import type { Alerte } from "../../types/alerte";
import type { Releve } from "../../types/releve";
import type { Seuil } from "../../types/seuil";

import "./TableauDeBord.css";

import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

export default function TableauDeBord() {
  const [localisations, setLocalisations] = useState<Localisation[]>([]);
  const [entrepots, setEntrepots] = useState<Entrepot[]>([]);
  const [chambres, setChambres] = useState<ChambreFroide[]>([]);
  const [conteneurs, setConteneurs] = useState<Conteneur[]>([]);
  const [alertes, setAlertes] = useState<Alerte[]>([]);
  const [releves, setReleves] = useState<Releve[]>([]);
  const [seuils, setSeuils] = useState<Seuil[]>([]);
  const [selectedChambre, setSelectedChambre] = useState<string | null>(null);

  useEffect(() => {
    const safeSetArray = <T,>(setter: React.Dispatch<React.SetStateAction<T[]>>) => (data: unknown) => {
      if (Array.isArray(data)) setter(data as T[]);
      else setter([]);
    };

    getAllLocalisations().then(safeSetArray<Localisation>(setLocalisations));
    getAllEntrepots().then(safeSetArray<Entrepot>(setEntrepots));
    getAllChambresFroides().then(safeSetArray<ChambreFroide>(setChambres));
    getConteneurs().then(safeSetArray<Conteneur>(setConteneurs));
    getAlertes().then(safeSetArray<Alerte>(setAlertes));
    getReleves().then(safeSetArray<Releve>(setReleves));
    getSeuils().then(safeSetArray<Seuil>(setSeuils));
  }, []);


  // ✅ Rotation automatique toutes les 5 secondes
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;

    if (!selectedChambre && chambres.length > 0) {
      let index = 0;
      interval = setInterval(() => {
        setSelectedChambre(chambres[index].id);
        index = (index + 1) % chambres.length;
      }, 5000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [selectedChambre, chambres]);

  const chambresEnService = chambres.filter(c => c.etat === true).length;
  const conteneursActifs = conteneurs.filter(c => c.etat === "S").length;
  const alertesRetard = alertes.filter(a => a.nature.toLowerCase().includes("retard")).length;
  const alertesHorsSeuil = alertes.filter(a => a.nature.toLowerCase().includes("seuil")).length;

  const relevesFiltres = selectedChambre
    ? releves.filter(r => r.chambre_froide.id === selectedChambre)
    : releves;

  const dates = Array.from(new Set(relevesFiltres.map(r => r.date))).slice(-7);

  const seuilChambre = selectedChambre
    ? seuils.find(s => s.chambre_froide.id === selectedChambre)
    : null;

  // ✅ Fonction utilitaire pour afficher "Chambre (Entrepôt)"
  const formatNomChambre = (c: ChambreFroide) => {
    const entrepotNom = c.entrepot?.nom ?? "";
    return entrepotNom ? `${c.nom} (${entrepotNom})` : c.nom;
  };

  const data = {
    labels: dates,
    datasets: [
      {
        label: selectedChambre && relevesFiltres[0]?.chambre_froide
          ? `Température ${formatNomChambre(relevesFiltres[0].chambre_froide)}`
          : "Températures chambres",
        data: dates.map(d => {
          const r = relevesFiltres.find(r => r.date === d);
          return r?.temperature ?? null;
        }),
        borderColor: "#2563eb",
        backgroundColor: "#2563eb",
        tension: 0.3
      },
      ...(seuilChambre ? [{
        label: "Seuil min",
        data: Array(dates.length).fill(seuilChambre.temperature_min),
        borderColor: "#10b981",
        borderDash: [6, 6],
        pointRadius: 0
      }] : []),
      ...(seuilChambre ? [{
        label: "Seuil max",
        data: Array(dates.length).fill(seuilChambre.temperature_max),
        borderColor: "#ef4444",
        borderDash: [6, 6],
        pointRadius: 0
      }] : [])
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" as const },
      title: { display: true, text: "Évolution des températures (7 jours)" }
    }
  };

  return (
    <div className="dashboard space-y-6">
      {/* Cartes rapides */}
      <div className="cards-rapides">
        <div className="stat-card">
          <div className="icon-badge blue"><FaMapMarkerAlt /></div>
          <p>Localisations actives</p>
          <h2>{localisations.length}</h2>
        </div>
        <div className="stat-card">
          <div className="icon-badge blue"><FaWarehouse /></div>
          <p>Entrepôts</p>
          <h2>{entrepots.length}</h2>
        </div>
        <div className="stat-card">
          <div className="icon-badge blue"><Snowflake /></div>
          <p>Chambres froides</p>
          <h2>{chambresEnService} / {chambres.length}</h2>
        </div>
        <div className="stat-card">
          <div className="icon-badge blue"><FaBox /></div>
          <p>Conteneurs</p>
          <h2>{conteneursActifs} / {conteneurs.length}</h2>
        </div>
      </div>

      {/* Graphe températures */}
      <div className="graph-card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="graph-title">Évolution des températures</h2>
          <select
            value={selectedChambre ?? ""}
            onChange={e => setSelectedChambre(e.target.value || null)}
            className="select-chambre"
          >
            <option value="">Toutes les chambres</option>
            {chambres.map(c => (
              <option key={c.id} value={c.id}>{formatNomChambre(c)}</option>
            ))}
          </select>
        </div>
        <div className="graph-wrapper">
          <Line data={data} options={options} />
        </div>
      </div>

      {/* Alertes côte à côte */}
      <div className="grid-alertes">
        <div className="alert-card retard">
          <div className="icon-badge orange"><Bell /></div>
          <p>Alertes retard de relève</p>
          <h2>{alertesRetard}</h2>
        </div>
        <div className="alert-card seuil">
          <div className="icon-badge red"><Bell /></div>
          <p>Alertes hors-seuil</p>
          <h2>{alertesHorsSeuil}</h2>
        </div>
      </div>
    </div>
  );
}
